import type { ReactNode } from 'react';
import { tr } from '@/i18n/tr';
import styles from './tabBar.module.scss';
import { AlertIcon, ChatIcon, HomeIcon, PlusIcon, UserIcon } from './tabBarIcons';

/** Tab ids follow the mockup order: Keşfet · Sohbet · +İlan Ver · Sorun Bildir · Profil. */
export type TabId = 'explore' | 'chat' | 'createListing' | 'report' | 'profile';

export interface TabBarProps {
  /** Highlighted tab; undefined on non-tab routes (auth screens). */
  active?: TabId;
  onSelect: (tab: TabId) => void;
  /**
   * Pressed when the centre button is tapped while the create screen is open,
   * where it reads as an x rather than a plus. Separate from `onSelect` because
   * that path treats re-selecting the active tab as a no-op and skips the
   * unsaved-work guard — which is exactly the case this is.
   */
  onCloseCreate?: () => void;
  /** Unread chat badge count (red dot from the mockups); 0 hides it. */
  chatBadge?: number;
  /** Answered-but-unopened reports; 0 hides it. */
  reportBadge?: number;
  /** Unread notifications. The bell lives inside Profil; 0 hides it. */
  profileBadge?: number;
}

interface TabDef {
  id: TabId;
  label: string;
  icon: ReactNode;
}

const tabs: TabDef[] = [
  { id: 'explore', label: tr.nav.explore, icon: <HomeIcon /> },
  { id: 'chat', label: tr.nav.chat, icon: <ChatIcon /> },
  { id: 'createListing', label: tr.nav.createListing, icon: <PlusIcon /> },
  { id: 'report', label: tr.nav.report, icon: <AlertIcon /> },
  { id: 'profile', label: tr.nav.profile, icon: <UserIcon /> },
];

/** Bottom navigation with the raised center FAB ("İlan Ver"), as in every mockup screen. */
export function TabBar({
  active,
  onSelect,
  onCloseCreate,
  chatBadge = 0,
  reportBadge = 0,
  profileBadge = 0,
}: TabBarProps) {
  const closing = active === 'createListing' && onCloseCreate !== undefined;

  return (
    <nav className={styles.tabBar} aria-label={tr.app.name}>
      {tabs.map((tab) =>
        tab.id === 'createListing' ? (
          <button
            key={tab.id}
            type="button"
            className={`${styles.fab} ${closing ? styles.fabActive : ''}`}
            aria-label={closing ? tr.common.close : tab.label}
            aria-current={active === tab.id ? 'page' : undefined}
            onClick={() => (closing ? onCloseCreate() : onSelect(tab.id))}
          >
            <span className={styles.fabIcon}>{tab.icon}</span>
          </button>
        ) : (
          <button
            key={tab.id}
            type="button"
            className={`${styles.tab} ${active === tab.id ? styles.active : ''}`}
            aria-current={active === tab.id ? 'page' : undefined}
            data-tab={tab.id}
            onClick={() => onSelect(tab.id)}
          >
            <span className={styles.lozenge} aria-hidden />
            <span className={styles.iconWrap}>
              <span className={styles.glyph}>{tab.icon}</span>
              {tab.id === 'chat' && chatBadge > 0 && (
                <span className={styles.badge}>{chatBadge}</span>
              )}
              {tab.id === 'report' && reportBadge > 0 && (
                <span className={styles.badge}>{reportBadge}</span>
              )}
              {tab.id === 'profile' && profileBadge > 0 && (
                <span className={styles.badge}>{profileBadge}</span>
              )}
            </span>
            <span className={styles.label}>{tab.label}</span>
          </button>
        )
      )}
    </nav>
  );
}
